/**
 * When to (re-)summarise a chat, and what the summary covers. Pure, so it can be tested without the database.
 *
 * The first summary is made at AUTO_SUMMARIZE_AT_MESSAGES exchanges and covers everything but the newest
 * KEEP_RECENT_MESSAGES. After that, once RESUMMARIZE_TAIL_MESSAGES messages have piled up after the covered part,
 * the old summary plus those messages are summarised again (chained), so what is sent per request stays bounded:
 * the server refuses more than 100 messages, and every message sent is paid for on every round.
 */

export const AUTO_SUMMARIZE_AT_MESSAGES = 30 // 15 exchanges
export const KEEP_RECENT_MESSAGES = 5
export const RESUMMARIZE_TAIL_MESSAGES = 20

export interface CompressionPlan {
  /** Index of the first message to summarise (the part before it is in `previousSummary`). */
  from: number
  /** Index after the last message to summarise; the new summary covers messages [0, to). */
  to: number
  previousSummary?: string
}

export function planCompression(
  messageCount: number,
  state: { isSummarized: boolean; summary?: string; summarizedCount?: number },
): CompressionPlan | null {
  const to = messageCount - KEEP_RECENT_MESSAGES
  if (!state.isSummarized) {
    if (messageCount < AUTO_SUMMARIZE_AT_MESSAGES) return null
    return to >= 3 ? { from: 0, to } : null
  }
  const covered = state.summarizedCount ?? 0
  if (messageCount - covered < RESUMMARIZE_TAIL_MESSAGES) return null
  if (to - covered < 3) return null
  return { from: covered, to, ...(state.summary ? { previousSummary: state.summary } : {}) }
}

/** The messages to send to /ai/summarize for a plan: the earlier summary first, then the new part. */
export function summarizeRequestMessages<T extends { role: string; content?: string }>(
  messages: T[],
  plan: CompressionPlan,
): { role: string; content: string }[] {
  const part = messages.slice(plan.from, plan.to).map((m) => ({ role: m.role, content: m.content ?? "" }))
  return plan.previousSummary
    ? [{ role: "user", content: `Summary of the conversation so far:\n${plan.previousSummary}` }, ...part]
    : part
}
